import { TimePicker } from "@mui/x-date-pickers";
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Grid,
  MenuItem,
  ButtonGroup,
  Menu,
  IconButton,
  MenuProps,
  Autocomplete,
  Chip
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Rating from "@mui/material/Rating";
import { styled } from "@mui/material/styles";
import { Add, Remove } from "@mui/icons-material";
import { merge } from "lodash";
import { tl, t } from "../../../components/translate";
import { medicineType } from "./medicineType";
import Modal from "../../../components/Modal/Modal";
import useMobileScreen from "../../../hooks/useMobileScreen";
import { addNewMedicine } from "../../../api/prescrption";
import { Medicine } from "../../../interfaces/AssessmentInterfaces";

const PARTS_OF_DAY = {
  MORNING: "morning",
  AFTERNOON: "afternoon",
  EVENING: "evening",
  NIGHT: "night"
};

const disableMedicationTimeForItems = [
  "Injection",
  "AMP",
  "Cream",
  "Drop",
  "Ear Drop",
  "Ear/Eye",
  "EYE",
  "Eye drop",
  "Eye Ointment",
  "Gel",
  "Inhaler",
  "Lotion",
  "Nasal Drop",
  "Nasal Spray",
  "Ointment",
  "PATCH",
  "Spray",
  "VIAL",
  "Facewash",
  "Serum",
  "Shampoo",
  "Toothpaste",
  "Bodywash",
  "Suncream",
  "Cleanser",
  "Creamy Gel",
  "Moisturizer"
];

export enum Times {
  HOUR = "hour",
  DAY = "day",
  WEEK = "week",
  MONTH = "month"
}

export enum DaysOfWeek {
  SUNDAY = "sunday",
  MONDAY = "monday",
  TUESDAY = "tuesday",
  WEDNESDAY = "wednesday",
  THURSDAY = "thursday",
  FRIDAY = "friday",
  SATURDAY = "saturday"
}

const timesOptions = Object.values(merge(Times, DaysOfWeek));

const disabledDayAndMonth = (times) => Object.values(DaysOfWeek).includes(times);

const isInjectionAndFrequencyNotSelected = (form: string, frequency: number) =>
  form === "Injection" && !frequency;

const extendedFrequencyForms = ["Drop", "Ear Drop", "Ear/Eye", "EYE", "Eye drop", "Nasal Drop"];

const getMaxFrequencyForForm = (form: string) => (extendedFrequencyForms.includes(form) ? 8 : 5);

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right"
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right"
    }}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
))(() => ({
  "& .MuiPaper-root": {
    boxShadow: "0px 4px 16px rgba(0, 150, 84, 0.16)",
    "& .MuiMenu-list": {
      padding: "4px 0"
    },
    height: "130px",
    overflow: "auto",
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": {
      width: "0.3em"
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "10px",
      marginTop: "10px",
      marginBottom: "10px"
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#d7d7d7",
      borderRadius: "10px"
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#d7d7d7"
    },
    borderRadius: 5
  }
}));

const FrequencyPicker = styled(Rating)({
  "& .MuiRating-iconFilled": {
    fill: "#ff3d47",
    stroke: "#ff3d47"
  },
  "& .MuiRating-iconHover": {
    fill: "#ff3d47",
    stroke: "#ff3d47"
  },
  "& .MuiRating-iconEmpty": {
    fill: "inherit",
    stroke: "inherit"
  }
});

const MedicationDialog = ({
  modalIsOpen,
  handleModalSave,
  handleCancel,
  data,
  spID = null,
  isProductMedication = false
}: {
  modalIsOpen: boolean;
  handleModalSave: (data: Medicine) => void;
  handleCancel: () => void;
  data: Medicine;
  spID?: number | string | null;
  isProductMedication?: boolean;
}): React.ReactNode => {
  const isMobileScreen = useMobileScreen();

  // local states
  const [brand, setBrand] = useState("");
  const [remarks, setRemarks] = useState("");
  const [form, setForm] = useState("Tablet");
  const [strength, setStrength] = useState("");
  const [frequency, setFrequency] = useState(0);
  const [genericName, setGenericName] = useState("");
  const [timesOfDay, setTimesOfDay] = useState<Array<string>>([]);
  const [selectedTime, setSelectedTime] = useState<any>(null);
  const [partsOfDay, setPartsOfDay] = useState<Array<string>>([]);
  const [times, setTimes] = useState(isProductMedication ? "" : "day");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [durationAmount, setDurationAmount] = useState(isProductMedication ? 0 : 7);
  const [durationFormat, setDurationFormat] = useState(isProductMedication ? "" : "day");
  const [beforeAfterMeal, setBeforeAfterMeal] = useState<string | null>(
    isProductMedication ? "" : "after"
  );

  // helper functions
  const handleSave = () => {
    if (data) {
      const newObj = {
        id: data.id,
        duration: durationAmount,
        form,
        meal: beforeAfterMeal || "",
        brand,
        remark: remarks,
        strength,
        frequency,
        genericName: genericName || data.genericName,
        frequencyType: durationFormat,
        times,
        partsOfDay: partsOfDay || [],
        timesOfDay: timesOfDay || []
      };

      const newMed = {
        medicineBrandId: data.id || null,
        brand,
        strength,
        serviceProviderId: spID || null,
        form
      };

      if (
        (data.brand !== brand || data.strength !== strength || data.form !== form) &&
        !isProductMedication
      ) {
        addNewMedicine(newMed);
      }
      // eslint-disable-next-line no-unused-expressions
      newObj && handleModalSave(newObj);
    }
  };

  const setInitialStateForMedication = () => {
    setBeforeAfterMeal("after");
    setDurationAmount(7);
    setDurationFormat("day");
    setFrequency(3);
    setRemarks("");
    setTimes("day");
    setGenericName("");
    setPartsOfDay([]);
    setTimesOfDay([]);
    setSelectedTime(null);
  };

  const setInitialStateForProductMedication = () => {
    setBeforeAfterMeal("");
    setDurationAmount(0);
    setDurationFormat("");
    setFrequency(0);
    setRemarks("");
    setTimes("");
    setGenericName("");
    setPartsOfDay([]);
    setTimesOfDay([]);
    setSelectedTime(null);
  };

  // use effects
  useEffect(() => {
    if (!data) return;

    setBrand(data.brand ?? "");
    setForm(data.form ?? "Tablet");
    setStrength(data.strength ?? "");
    setGenericName(data.genericName ?? "");
    setRemarks(data.remark ?? "");

    // frequency
    if (data.form === "Injection") {
      setFrequency(0);
    } else {
      setFrequency(data.frequency ?? (isProductMedication ? 0 : 3));
    }

    // duration
    setDurationAmount(data.duration ?? (isProductMedication ? 0 : 7));
    setDurationFormat(data.frequencyType ?? (isProductMedication ? "" : "day"));

    // times
    setTimes(data.times ?? (isProductMedication ? "" : "day"));

    // meal
    if (disableMedicationTimeForItems.includes(data.form ?? "") || isProductMedication) {
      setBeforeAfterMeal(null);
    } else {
      setBeforeAfterMeal(data.meal ?? "after");
    }

    // parts of day / times of day
    setPartsOfDay(
      // eslint-disable-next-line no-nested-ternary
      data.partsOfDay && Array.isArray(data.partsOfDay) && data.partsOfDay.length > 0
        ? data.partsOfDay
        : isProductMedication
        ? []
        : [PARTS_OF_DAY.MORNING]
    );

    if (data.timesOfDay && Array.isArray(data.timesOfDay) && data.timesOfDay.length > 0) {
      setTimesOfDay(data.timesOfDay);
    } else {
      setTimesOfDay([]);
    }

    setSelectedTime(null);
  }, [data, isProductMedication]);

  useEffect(() => {
    const maxFrequency = getMaxFrequencyForForm(form);

    if (frequency > maxFrequency) {
      setFrequency(maxFrequency);
    }
  }, [form, frequency]);

  return (
    <>
      <Modal
        open={modalIsOpen}
        onClose={() => {
          handleCancel();
          if (!isProductMedication) {
            setInitialStateForMedication();
          } else {
            setInitialStateForProductMedication();
          }
        }}
        onSave={() => {
          handleSave();
          if (!isProductMedication) {
            setInitialStateForMedication();
          } else {
            setInitialStateForProductMedication();
          }
        }}
        title={tl("assessment.createNewMedication")}
      >
        <Grid container spacing={1}>
          <Grid size={{ xs: 12, sm: 7 }}>
            <TextField
              label={t("assessment.medicineName")}
              variant="outlined"
              size="small"
              fullWidth
              value={brand}
              onChange={(e) => {
                setBrand(e.target.value);
              }}
              data-testmation="medicationNameTextField"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Box pl={2} display="flex" alignItems="center" justifyContent="center">
              <TextField
                label={t("assessment.medicationStrength")}
                variant="outlined"
                size="small"
                fullWidth
                value={strength}
                onChange={(e) => setStrength(e.target.value)}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="center" pl={2}>
              <Autocomplete
                options={Object.keys(medicineType).sort((a, b) => a.localeCompare(b))}
                value={form}
                fullWidth
                onChange={(e, v) => {
                  setForm(v || "Tablet");
                  if (disableMedicationTimeForItems.includes(v || "Tablet")) {
                    setBeforeAfterMeal(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("type")}
                    variant="outlined"
                    size="small"
                    fullWidth
                    data-testmation="medicationTypeDropDown"
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid size={12}>
            {isProductMedication ? (
              <TextField
                variant="outlined"
                value={genericName}
                label="Generic name"
                onChange={({ target }) => setGenericName(target.value)}
              />
            ) : (
              data.genericName && (
                <Grid size={12}>
                  <Box pl={1} pt="4px" fontSize="0.7rem">
                    {data.genericName}
                  </Box>
                </Grid>
              )
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                pt: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2
              }}
            >
              <Box>
                <Typography sx={{ mb: 0.5 }}>
                  <b>Frequency</b>
                </Typography>

                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <FrequencyPicker
                    name="simple-controlled"
                    value={frequency}
                    max={getMaxFrequencyForForm(form)}
                    size="large"
                    onChange={(e, v) => {
                      setFrequency(v);
                    }}
                    icon={(form && medicineType[form]()) || medicineType.Tablet()}
                    emptyIcon={form && medicineType[form]()}
                    data-testmation="frequencyPickerSelector"
                  />
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {tl("assessment.times")}
                    <Box display="flex" alignItems="center">
                      <Typography>{times}</Typography>
                      <IconButton
                        size="small"
                        sx={{ cursor: "pointer" }}
                        onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </Box>
                    <StyledMenu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={() => setAnchorEl(null)}
                    >
                      {timesOptions.map((item) => (
                        <MenuItem
                          onClick={() => {
                            setTimes(item);
                            if (Object.values(DaysOfWeek).includes(item as DaysOfWeek)) {
                              setDurationFormat("week");
                            } else {
                              setDurationFormat(item);
                            }
                            setAnchorEl(null);
                          }}
                          key={item}
                          value={item}
                        >
                          {item}
                        </MenuItem>
                      ))}
                    </StyledMenu>
                  </Box>
                </Box>
              </Box>

              <Box>
                <Typography sx={{ mb: 0.5 }}>
                  <b>Part of the Day</b>
                </Typography>
                <ButtonGroup size="small">
                  {Object.values(PARTS_OF_DAY).map((tod) => (
                    <Button
                      key={tod}
                      variant={partsOfDay.includes(tod) ? "contained" : "outlined"}
                      onClick={() => {
                        if (partsOfDay.includes(tod)) {
                          setPartsOfDay(partsOfDay.filter((v) => v !== tod));
                        } else {
                          setPartsOfDay([...partsOfDay, tod]);
                        }
                      }}
                      data-testmation="partsOfDay"
                    >
                      {tl(tod)}
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box pt={2}>
              <Typography sx={{ mb: 0.5 }}>
                <b>Meal Timing</b>
              </Typography>

              <ButtonGroup
                size="small"
                aria-label="small outlined button group"
                disabled={disableMedicationTimeForItems.includes(form)}
              >
                <Button
                  variant={beforeAfterMeal === "before" ? "contained" : "outlined"}
                  onClick={() => {
                    if (beforeAfterMeal === "before") {
                      setBeforeAfterMeal("");
                    } else {
                      setBeforeAfterMeal("before");
                    }
                  }}
                  data-testmation="medicationBeforeMealButton"
                >
                  {tl("assessment.beforeMeal")}
                </Button>
                <Button
                  variant={beforeAfterMeal === "after" ? "contained" : "outlined"}
                  onClick={() => {
                    if (beforeAfterMeal === "after") {
                      setBeforeAfterMeal("");
                    } else {
                      setBeforeAfterMeal("after");
                    }
                  }}
                  data-testmation="medicationAfterMealButton"
                >
                  {tl("assessment.afterMeal")}
                </Button>
              </ButtonGroup>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box>
              <Typography sx={{ mb: 0.5 }}>
                <b>Time</b>
              </Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <TimePicker
                  value={selectedTime}
                  onChange={(newValue: any) => {
                    setSelectedTime(newValue);
                  }}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      size: "small",
                      placeholder: "8:00 AM",
                      sx: {
                        width: "120px",
                        "& .MuiInputBase-root": { fontSize: "12px" }
                      }
                    }
                  }}
                />

                <Button
                  variant="outlined"
                  onClick={() => {
                    if (selectedTime && typeof selectedTime.format === "function") {
                      const formatted = selectedTime.format("h:mm A");
                      if (formatted && !timesOfDay.includes(formatted)) {
                        setTimesOfDay([...timesOfDay, formatted]);
                      }
                      setSelectedTime(null);
                    }
                  }}
                  data-testmation="time"
                >
                  Add
                </Button>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  {timesOfDay.map((tVal) => (
                    <Chip
                      key={tVal}
                      label={tVal}
                      size="small"
                      onDelete={() => {
                        setTimesOfDay(timesOfDay.filter((tm) => tm !== tVal));
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid size={12}>
            <Typography sx={{ mb: 0.5, mt: 2 }}>
              <b>{tl("assessment.duration")}</b>
            </Typography>

            <Box display="flex" flexDirection={isMobileScreen ? "column" : "row"}>
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setDurationAmount(Number(durationAmount) - 1)}
                  disabled={
                    durationAmount <= 1 || isInjectionAndFrequencyNotSelected(form, frequency)
                  }
                  data-testmation="medicationAddDaysButton"
                >
                  <Remove />
                </Button>

                <TextField
                  size="small"
                  type="number"
                  variant="outlined"
                  color="primary"
                  value={durationAmount}
                  slotProps={{
                    htmlInput: { style: { textAlign: "center", width: "50px" } }
                  }}
                  onChange={(e) => setDurationAmount(Number(e.target.value))}
                  data-testmation="medicationSetDaysTextField"
                  disabled={isInjectionAndFrequencyNotSelected(form, frequency)}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 29
                    }
                  }}
                />

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setDurationAmount(Number(durationAmount) + 1)}
                  data-testmation="medicationRemoveDaysButton"
                  disabled={isInjectionAndFrequencyNotSelected(form, frequency)}
                >
                  <Add />
                </Button>
              </Box>

              <Box pl={2} pt={isMobileScreen && "16px"}>
                <ButtonGroup
                  size="small"
                  aria-label="small outlined button group"
                  disabled={isInjectionAndFrequencyNotSelected(form, frequency)}
                >
                  <Button
                    size="small"
                    variant={durationFormat === "day" ? "contained" : "outlined"}
                    onClick={() => setDurationFormat("day")}
                    data-testmation="medicationDurationDaysButton"
                    disabled={times === "week" || times === "month" || disabledDayAndMonth(times)}
                  >
                    {tl("assessment.days")}
                  </Button>

                  <Button
                    size="small"
                    variant={durationFormat === "week" ? "contained" : "outlined"}
                    onClick={() => setDurationFormat("week")}
                    data-testmation="medicationDurationWeeksButton"
                    disabled={times === "month"}
                  >
                    {tl("assessment.week")}
                  </Button>

                  <Button
                    size="small"
                    variant={durationFormat === "month" ? "contained" : "outlined"}
                    onClick={() => setDurationFormat("month")}
                    data-testmation="medicationDurationMonthsButton"
                    disabled={disabledDayAndMonth(times)}
                  >
                    {tl("assessment.month")}
                  </Button>
                </ButtonGroup>
              </Box>
            </Box>
          </Grid>

          <Grid size={12}>
            <Box pt={2}>
              <TextField
                label={t("assessment.medicationRemark")}
                variant="outlined"
                size="small"
                fullWidth
                value={remarks}
                slotProps={{
                  htmlInput: { maxLength: 200 },
                  inputLabel: { shrink: true }
                }}
                onChange={(e) => setRemarks(e.target.value)}
                data-testmation="medicationRemarksTextField"
              />
            </Box>
          </Grid>
        </Grid>
      </Modal>
    </>
  );
};

export default MedicationDialog;
